/**
 * <zumii-eyes> - interactive ZUMII logo eyes.
 *
 * States: OPEN (default/idle), CLOSED (blink), WINK (one eye), TRACK
 * (pupils follow the pointer). These are expressed as transient CSS custom
 * property changes on each [data-eye] element rather than a persistent
 * state string, since at most one of "blinking" / "tracking" is ever
 * visually true at a time and they compose (a blink can happen mid-track).
 *
 * If this script never runs, --zumii-eye-lid/--zumii-pupil-* are unset and
 * the CSS var() fallbacks render OPEN with centered pupils - the logo is
 * never broken or hidden by a JS failure.
 */
(() => {
  if (customElements.get('zumii-eyes')) return;

  const LID_OPEN = 1;
  const LID_CLOSED = 0.12;
  const BLINK_DURATION = 140;
  const WINK_DURATION = 180;

  class ZumiiEyes extends HTMLElement {
    connectedCallback() {
      this.eyes = Array.from(this.querySelectorAll('[data-eye]'));
      if (!this.eyes.length) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.canTrack = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      if (this.reducedMotion) {
        this._setLids(LID_OPEN, this.eyes);
        this._setPupils(0, 0);
        return;
      }

      this._rects = [];
      this._raf = null;
      this._pointer = null;

      this._measure();
      this._onResize = this._measure.bind(this);
      window.addEventListener('resize', this._onResize, { passive: true });

      if (this.canTrack) {
        this._onPointerMove = this._handlePointerMove.bind(this);
        this._onPointerLeave = this._handlePointerLeave.bind(this);
        window.addEventListener('pointermove', this._onPointerMove, { passive: true });
        document.addEventListener('pointerleave', this._onPointerLeave, { passive: true });
      }

      this._scheduleBlink();
      if (this.canTrack) this._scheduleWink();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      if (this._onPointerMove) window.removeEventListener('pointermove', this._onPointerMove);
      if (this._onPointerLeave) document.removeEventListener('pointerleave', this._onPointerLeave);
      clearTimeout(this._blinkTimer);
      clearTimeout(this._winkTimer);
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    _measure() {
      this._rects = this.eyes.map((eye) => eye.getBoundingClientRect());
    }

    _handlePointerMove(event) {
      this._pointer = { x: event.clientX, y: event.clientY };
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._track();
      });
    }

    _handlePointerLeave() {
      this._pointer = null;
      this._setPupils(0, 0);
    }

    _track() {
      if (!this._pointer) return;
      this.eyes.forEach((eye, index) => {
        const rect = this._rects[index];
        if (!rect || !rect.width) return;

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dx = this._pointer.x - centerX;
        const dy = this._pointer.y - centerY;
        const distance = Math.hypot(dx, dy) || 1;

        // Clamp how far the pupil can travel (stays well inside the eye
        // white) and ease movement in over the first few eye-widths so the
        // pupils don't snap to max offset when the pointer is far away.
        const maxOffset = rect.width * 0.16;
        const ease = Math.min(distance / (rect.width * 4), 1);
        const x = (dx / distance) * maxOffset * ease;
        const y = (dy / distance) * maxOffset * ease;

        eye.style.setProperty('--zumii-pupil-x', `${x}px`);
        eye.style.setProperty('--zumii-pupil-y', `${y}px`);
      });
    }

    _setPupils(x, y) {
      this.eyes.forEach((eye) => {
        eye.style.setProperty('--zumii-pupil-x', `${x}px`);
        eye.style.setProperty('--zumii-pupil-y', `${y}px`);
      });
    }

    _setLids(value, eyes) {
      eyes.forEach((eye) => eye.style.setProperty('--zumii-eye-lid', value));
    }

    _blink() {
      this._setLids(LID_CLOSED, this.eyes);
      setTimeout(() => this._setLids(LID_OPEN, this.eyes), BLINK_DURATION);
    }

    _scheduleBlink() {
      const delay = 3200 + Math.random() * 4200;
      this._blinkTimer = setTimeout(() => {
        this._blink();
        this._scheduleBlink();
      }, delay);
    }

    _wink() {
      const eye = this.eyes[Math.random() < 0.5 ? 0 : 1];
      this._setLids(LID_CLOSED, [eye]);
      setTimeout(() => this._setLids(LID_OPEN, [eye]), WINK_DURATION);
    }

    _scheduleWink() {
      const delay = 26000 + Math.random() * 20000;
      this._winkTimer = setTimeout(() => {
        this._wink();
        this._scheduleWink();
      }, delay);
    }
  }

  customElements.define('zumii-eyes', ZumiiEyes);
})();
